import { PropsWithChildren, useEffect } from 'react';
import Box from '@mui/material/Box';
import { useAuth0 } from '@auth0/auth0-react';
import { Typography } from '@mui/material';
import { PageLoader } from './shared/PageLoader';
import { auth0TokenState, userSyncedState } from '@/state';
import { useAtom } from 'jotai';
import { saveRedirectUrl } from '@/utils/auth';
import { LoginTypeEnum } from '@/__generated__/graphql';

export const AuthGuard = ({ children }: PropsWithChildren<{}>) => {
  const [userSynced, setUserSynced] = useAtom(userSyncedState);
  const [authToken, setAuthToken] = useAtom(auth0TokenState);
  const {
    error,
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    loginWithRedirect,
    user,
    logout,
  } = useAuth0();

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath !== '/api/auth/callback') {
        saveRedirectUrl(currentPath);
      }
      loginWithRedirect();
    }
  }, [isAuthenticated, isLoading, loginWithRedirect]);

  useEffect(() => {
    if (isLoading) return;

    if (isAuthenticated && !authToken) {
      getAccessTokenSilently({ cacheMode: 'off' })
        .then((token) => {
          if (token && token.length > 0) {
            setAuthToken(token);
          } else {
            setAuthToken('');
            logout();
          }
        })
        .catch((error) => {
          console.error('Failed to fetch token:', error);
          setAuthToken('');
          logout();
        });
    }
  }, [isLoading, isAuthenticated, authToken, getAccessTokenSilently, setAuthToken, logout]);

  useEffect(() => {
    const syncUser = async () => {
      if (!authToken || !isAuthenticated || userSynced) return;

      if (!user) return;

      const {
        name,
        picture,
        email,
        sub: userId,
        'http://abyss.com/user/connectionName': connection,
        'http://abyss.com/user/roles': roles,
        'http://abyssfabric.co/user/permissions': permissions,
      } = user || {};

      try {
        const { Google, Email, Microsoft } = LoginTypeEnum;
        const response = await fetch(new URL('/graphql', import.meta.env.VITE_APP_API_URL).href, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query: `
            mutation UpdateUserInfo($input: UpdateUserInfoInput) {
              updateUserInfo(input: $input) {
                id
              }
            }
          `,
            variables: {
              input: {
                userId,
                connection,
                email,
                name,
                picture,
                roles,
                permissions,
                status: 'ACTIVE',
                loginType: (() => {
                  let loginType;
                  if (connection === 'google-oauth2') {
                    loginType = Google;
                  } else if (connection === 'BP-Azure-AD') {
                    loginType = Microsoft;
                  } else {
                    loginType = Email;
                  }
                  return loginType;
                })(),
              },
            },
          }),
        });

        if (!response.ok) {
          const responseJSON = await response.json();
          throw new Error(responseJSON?.errors?.[0]?.message ?? response.statusText);
        }

        setUserSynced(true);
      } catch (error) {
        console.error('Error syncing user:', error);
        setAuthToken('');
        logout();
      }
    };

    syncUser();
  }, [authToken, isAuthenticated, user, userSynced, setUserSynced, logout, setAuthToken]);

  if (isAuthenticated && authToken && userSynced) {
    return <>{children}</>;
  }

  if (error) {
    return <Typography sx={{ mt: 10 }}>Error Logging in: {error?.message}</Typography>;
  }

  return (
    <Box sx={{ position: 'relative', top: '25rem' }}>
      <PageLoader />
    </Box>
  );
};
